const CACHE_KEY_PREFIX = 'forecast_cache_';
const DEFAULT_TTL_HOURS = 6;

const SUPABASE_URL      = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

export type ForecastPeriod = 'today' | 'week' | 'month';

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

/** In-memory cache layer (L1), cleared on page reload. */
const memCache = new Map<string, CacheEntry<unknown>>();

function makeKey(feature: string, params: Record<string, string>): string {
  const sorted = Object.entries(params).sort(([a], [b]) => a.localeCompare(b));
  return `${CACHE_KEY_PREFIX}${feature}_${sorted.map(([k, v]) => `${k}=${v}`).join('_')}`;
}

/** Read from L1 (memory) → L2 (localStorage) cache. Returns null on miss. */
export function readCache<T>(feature: string, params: Record<string, string>): T | null {
  const key = makeKey(feature, params);
  const now = Date.now();

  // L1 check
  const mem = memCache.get(key);
  if (mem && mem.expiresAt > now) return mem.data as T;

  // L2 check
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const entry: CacheEntry<T> = JSON.parse(raw);
    if (entry.expiresAt > now) {
      memCache.set(key, entry); // warm L1
      return entry.data;
    }
    localStorage.removeItem(key);
  } catch {
    // ignore parse errors
  }

  return null;
}

/** Write to both L1 (memory) and L2 (localStorage) cache. */
export function writeCache<T>(
  feature: string,
  params: Record<string, string>,
  data: T,
  ttlHours = DEFAULT_TTL_HOURS,
): void {
  const key = makeKey(feature, params);
  const expiresAt = Date.now() + ttlHours * 60 * 60 * 1000;
  const entry: CacheEntry<T> = { data, expiresAt };

  memCache.set(key, entry as CacheEntry<unknown>);

  try {
    localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // localStorage quota exceeded, silently skip L2
  }
}

/** Call the public-forecast edge function without requiring a user JWT. */
async function fetchPublicForecastRaw(
  sign: string,
  period: ForecastPeriod,
  language: string,
): Promise<string | null> {
  try {
    const resp = await fetch(`${SUPABASE_URL}/functions/v1/public-forecast`, {
      method:  'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey':        SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ sign, period, language }),
    });
    if (!resp.ok) return null;
    const raw = await resp.json().catch(() => null);
    // Unwrap { ok: true, data: { text: '...' } } envelope
    const data = raw?.ok ? raw.data : raw;
    return (data?.text as string) || null;
  } catch {
    return null;
  }
}

/** Fetch a public forecast from the cache, or call the edge function and cache result. */
export async function getCachedForecast(
  sign: string,
  period: ForecastPeriod = 'today',
  language = 'pt-BR',
  isAdmin = false,
): Promise<string | null> {
  const params = { sign, period, language };
  if (!isAdmin) {
    const cached = readCache<{ text: string }>('public-forecast', params);
    if (cached?.text) return cached.text;
  }

  const text = await fetchPublicForecastRaw(sign, period, language);
  if (!text) return null;

  if (!isAdmin) {
    writeCache('public-forecast', params, { text });
  }
  return text;
}
